import React from "react";
import * as LucideIcons from "lucide-react";

/**
 * Audited application-icon aliases. Brand marks, provider logos, and chart
 * series artwork intentionally stay with their domain owners.
 */
const iconAliases = Object.freeze({
  Activity: "Activity",
  AlertCircle: "AlertCircle",
  AlertTriangle: "AlertTriangle",
  ArrowDown: "ArrowDown",
  ArrowDownLeft: "ArrowDownLeft",
  ArrowDownRight: "ArrowDownRight",
  ArrowLeft: "ArrowLeft",
  ArrowLeftRight: "ArrowLeftRight",
  ArrowRight: "ArrowRight",
  ArrowUp: "ArrowUp",
  ArrowUpDown: "ArrowUpDown",
  Award: "Award",
  BadgeCheck: "BadgeCheck",
  BarChart3: "BarChart3",
  Bell: "Bell",
  BellOff: "BellOff",
  Bookmark: "Bookmark",
  BookOpen: "BookOpen",
  Bot: "Bot",
  Building: "Building",
  Building2: "Building2",
  Calendar: "Calendar",
  CalendarRange: "CalendarRange",
  Camera: "Camera",
  ChartBar: "BarChart3",
  Check: "Check",
  CheckCircle: "CircleCheck",
  CheckCircle2: "CircleCheckBig",
  CheckSquare: "SquareCheck",
  ChevronDown: "ChevronDown",
  ChevronLeft: "ChevronLeft",
  ChevronRight: "ChevronRight",
  ChevronUp: "ChevronUp",
  ChevronsLeft: "ChevronsLeft",
  ChevronsRight: "ChevronsRight",
  Circle: "Circle",
  CircleDot: "CircleDot",
  Clock: "Clock",
  ClockArrowDown: "ClockArrowDown",
  Code: "Code",
  Copy: "Copy",
  CreditCard: "CreditCard",
  Crown: "Crown",
  DollarSign: "DollarSign",
  Download: "Download",
  Edit: "Pencil",
  ExternalLink: "ExternalLink",
  Eye: "Eye",
  EyeOff: "EyeOff",
  FileText: "FileText",
  Flag: "Flag",
  Folder: "Folder",
  Globe: "Globe",
  Gift: "Gift",
  GripVertical: "GripVertical",
  Hash: "Hash",
  Heart: "Heart",
  HelpCircle: "CircleHelp",
  History: "History",
  Home: "Home",
  Image: "Image",
  Info: "Info",
  Key: "Key",
  Languages: "Languages",
  Layers: "Layers",
  Layout: "Layout",
  LayoutTemplate: "LayoutTemplate",
  LineChart: "ChartLine",
  Link: "Link",
  Link2: "Link2",
  Lightbulb: "Lightbulb",
  Loader: "Loader2",
  Loader2: "Loader2",
  Lock: "Lock",
  LogOut: "LogOut",
  Mail: "Mail",
  Map: "Map",
  MapPin: "MapPin",
  Maximize2: "Maximize2",
  Megaphone: "Megaphone",
  Menu: "Menu",
  MessageCircle: "MessageCircle",
  MessageSquare: "MessageSquare",
  Minus: "Minus",
  Monitor: "Monitor",
  Moon: "Moon",
  MoreHorizontal: "Ellipsis",
  MoreVertical: "EllipsisVertical",
  MousePointer: "MousePointer",
  MousePointerClick: "MousePointerClick",
  Move: "Move",
  Package: "Package",
  Palette: "Palette",
  PanelTop: "PanelTop",
  Pencil: "Pencil",
  Percent: "Percent",
  Phone: "Phone",
  Plus: "Plus",
  Puzzle: "Puzzle",
  RefreshCcw: "RefreshCcw",
  RefreshCw: "RefreshCw",
  Rocket: "Rocket",
  RotateCcw: "RotateCcw",
  Save: "Save",
  Search: "Search",
  SearchX: "SearchX",
  Send: "Send",
  Settings: "Settings",
  Share2: "Share2",
  Shield: "Shield",
  ShieldCheck: "ShieldCheck",
  ShoppingBag: "ShoppingBag",
  ShoppingCart: "ShoppingCart",
  Smartphone: "Smartphone",
  Square: "Square",
  Star: "Star",
  Stethoscope: "Stethoscope",
  StopCircle: "CircleStop",
  Store: "Store",
  Sun: "Sun",
  Tag: "Tag",
  ThumbsUp: "ThumbsUp",
  ToggleLeft: "ToggleLeft",
  ToggleRight: "ToggleRight",
  Trash: "Trash2",
  Trash2: "Trash2",
  TrendingUp: "TrendingUp",
  Truck: "Truck",
  Unlink: "Unlink",
  Unlock: "Unlock",
  Upload: "Upload",
  UploadCloud: "CloudUpload",
  User: "User",
  UserCheck: "UserCheck",
  UserCog: "UserCog",
  UserPlus: "UserPlus",
  UserX: "UserX",
  Users: "Users",
  Wallet: "Wallet",
  Webhook: "Webhook",
  Wrench: "Wrench",
  X: "X",
  XCircle: "CircleX",
  Zap: "Zap",
});

const Icon = ({ name, size, color, className = "", strokeWidth, ...props }) => {
  const resolvedName = iconAliases[name];
  const IconComponent = resolvedName ? LucideIcons[resolvedName] : undefined;

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in the audited Lucide aliases`);
    return null;
  }

  const hasAccessibleName = Boolean(
    props["aria-label"] || props["aria-labelledby"],
  );

  return (
    <IconComponent
      size={size || 16}
      color={color || "currentColor"}
      strokeWidth={strokeWidth ?? 1.75}
      className={className}
      aria-hidden={props["aria-hidden"] ?? !hasAccessibleName}
      focusable="false"
      role={hasAccessibleName ? "img" : undefined}
      {...props}
    />
  );
};

Icon.displayName = "Icon";

export { Icon, iconAliases };
